<template>
  <div v-if="!isOnline" class="offline-banner">
    <i class="mdi mdi-wifi-off"></i>
    You're offline — sales at the POS will queue and auto-sync when you're back online.
  </div>
  <div v-if="pendingSync > 0" class="sync-banner" :class="{ 'sync-banner-syncing': syncing }">
    <i class="mdi mdi-cloud-upload-outline"></i>
    <span>{{ syncing ? 'Syncing…' : pendingSync + ' offline sale(s) waiting to sync' }}</span>
    <button v-if="!syncing" class="sync-now-btn" @click="triggerSync">Sync now</button>
  </div>
  <nav class="nav">
    <div class="nav-brand" @click="$router.push('/dashboard')">
      <img src="/assets/images/paysol_logo.png" alt="Paysol" class="brand-logo" />
    </div>
    <ul class="nav-items">
      <li :class="{active: isActive('/dashboard')}"><router-link to="/dashboard">Dashboard</router-link></li>

      <!-- Restaurant dropdown (only when tenant is restaurant/mixed) -->
      <li v-if="isRestaurantMode && can('sales.create')" class="drop" :class="{active: isRestaurant}">
        <a @click="toggleDrop('restaurant')">Restaurant <i class="mdi mdi-chevron-down"></i></a>
        <div v-if="openDrop === 'restaurant'" class="drop-menu">
          <router-link to="/tables"  @click="openDrop=null">Tables</router-link>
          <router-link to="/tabs"    @click="openDrop=null">Tabs</router-link>
          <router-link to="/kitchen" @click="openDrop=null">Kitchen display</router-link>
        </div>
      </li>

      <!-- Tabs shortcut for non-restaurant tenants (salon walk-ins, retail hold) -->
      <li v-if="!isRestaurantMode && can('sales.create')" :class="{active: isActive('/tabs')}">
        <router-link to="/tabs">Tabs</router-link>
      </li>

      <!-- Services / salon dropdown (only when tenant is service/mixed) -->
      <li v-if="isServiceMode && can('sales.create')" class="drop" :class="{active: isServices}">
        <a @click="toggleDrop('services')">Services <i class="mdi mdi-chevron-down"></i></a>
        <div v-if="openDrop === 'services'" class="drop-menu">
          <router-link to="/appointments" @click="openDrop=null">Appointments</router-link>
        </div>
      </li>

      <!-- Sales dropdown -->
      <li v-if="can('sales.create')" class="drop" :class="{active: isSales}">
        <a @click="toggleDrop('sales')">Sales <i class="mdi mdi-chevron-down"></i></a>
        <div v-if="openDrop === 'sales'" class="drop-menu">
          <router-link to="/pos"    @click="openDrop=null">POS terminal</router-link>
          <router-link v-if="can('laybys.view')" to="/laybys" @click="openDrop=null">Laybys</router-link>
        </div>
      </li>

      <!-- Catalog dropdown -->
      <li v-if="can('products.view')" class="drop" :class="{active: isCatalog}">
        <a @click="toggleDrop('catalog')">Catalog <i class="mdi mdi-chevron-down"></i></a>
        <div v-if="openDrop === 'catalog'" class="drop-menu">
          <router-link to="/products" @click="openDrop=null">Products</router-link>
          <router-link to="/catalog"  @click="openDrop=null">Categories &amp; Brands</router-link>
          <router-link to="/barcodes" @click="openDrop=null">Barcode labels</router-link>
          <router-link to="/discounts" @click="openDrop=null">Discount codes</router-link>
          <router-link to="/price-rules" @click="openDrop=null">Price rules (happy hour)</router-link>
        </div>
      </li>

      <!-- Stock dropdown -->
      <li v-if="can('stock.view')" class="drop" :class="{active: isStock}">
        <a @click="toggleDrop('stock')">Stock <i class="mdi mdi-chevron-down"></i></a>
        <div v-if="openDrop === 'stock'" class="drop-menu">
          <router-link to="/stock" @click="openDrop=null">Stock levels</router-link>
          <router-link v-if="can('stock.take')"     to="/stock-takes"     @click="openDrop=null">Stock takes</router-link>
          <router-link v-if="can('stock.transfer')" to="/stock-transfers" @click="openDrop=null">Stock transfers</router-link>
        </div>
      </li>

      <!-- Purchasing dropdown -->
      <li v-if="can('suppliers.view') || can('po.view')" class="drop" :class="{active: isPurchasing}">
        <a @click="toggleDrop('purchasing')">Purchasing <i class="mdi mdi-chevron-down"></i></a>
        <div v-if="openDrop === 'purchasing'" class="drop-menu">
          <router-link v-if="can('suppliers.view')" to="/suppliers"       @click="openDrop=null">Suppliers</router-link>
          <router-link v-if="can('po.view')"        to="/purchase-orders" @click="openDrop=null">Purchase orders</router-link>
        </div>
      </li>

      <li v-if="can('customers.view')" :class="{active: isActive('/customers')}"><router-link to="/customers">Customers</router-link></li>
      <li v-if="can('reports.view')" :class="{active: isActive('/reports')}"><router-link to="/reports">Reports</router-link></li>
      <li v-if="can('users.view')" :class="{active: isActive('/users')}"><router-link to="/users">Users</router-link></li>
      <li v-if="can('audit.view')" :class="{active: isActive('/audit')}"><router-link to="/audit">Audit</router-link></li>
      <li :class="{active: isActive('/notifications')}"><router-link to="/notifications">Notifications</router-link></li>
      <li v-if="isPlatformOwner" :class="{active: isActive('/admin')}"><router-link to="/admin" class="platform-link"><i class="mdi mdi-shield-crown"></i> Platform</router-link></li>
      <li :class="{active: isActive('/settings')}"><router-link to="/settings">Settings</router-link></li>
    </ul>
    <div class="nav-user">
      <!-- GLOBAL SEARCH -->
      <div class="nav-search">
        <i class="mdi mdi-magnify"></i>
        <input v-model="searchQ" @input="onSearchInput" @keydown.esc="closeSearch" @focus="onSearchFocus" placeholder="Search products, customers, receipts…" />
        <div v-if="showSearch && (searchResults || searchLoading)" class="search-dropdown" @click.stop>
          <div v-if="searchLoading" class="search-empty">Searching…</div>
          <template v-else>
            <div v-if="searchResults.products.length" class="search-group">
              <div class="search-group-title">Products</div>
              <router-link v-for="r in searchResults.products" :key="'p'+r.id" to="/products" @click="closeSearch" class="search-item">
                <i class="mdi mdi-package-variant"></i>
                <div><strong>{{ r.name }}</strong> <span class="muted small">{{ r.category_name ? '· ' + r.category_name : '' }}</span></div>
              </router-link>
            </div>
            <div v-if="searchResults.customers.length" class="search-group">
              <div class="search-group-title">Customers</div>
              <router-link v-for="r in searchResults.customers" :key="'c'+r.id" to="/customers" @click="closeSearch" class="search-item">
                <i class="mdi mdi-account-outline"></i>
                <div><strong>{{ r.name || 'Unnamed' }}</strong> <span class="muted small">· {{ r.phone || r.email || '' }}</span></div>
              </router-link>
            </div>
            <div v-if="searchResults.sales.length" class="search-group">
              <div class="search-group-title">Sales</div>
              <router-link v-for="r in searchResults.sales" :key="'s'+r.id" to="/reports" @click="closeSearch" class="search-item">
                <i class="mdi mdi-receipt-text-outline"></i>
                <div><code>{{ r.receipt_no }}</code> <span class="muted small">· KES {{ format(r.total) }} · {{ r.status }} · {{ r.customer_name || 'walk-in' }}</span></div>
              </router-link>
            </div>
            <div v-if="searchResults.appointments.length" class="search-group">
              <div class="search-group-title">Appointments</div>
              <router-link v-for="r in searchResults.appointments" :key="'a'+r.id" to="/appointments" @click="closeSearch" class="search-item">
                <i class="mdi mdi-calendar-check"></i>
                <div><strong>{{ r.service }}</strong> <span class="muted small">· {{ r.guest_name || 'customer' }} · {{ r.start_at }} · {{ r.status }}</span></div>
              </router-link>
            </div>
            <div v-if="!hasResults" class="search-empty">No matches. Try a different query.</div>
          </template>
        </div>
      </div>

      <!-- USER AVATAR DROPDOWN -->
      <div class="user-avatar-wrap drop" :class="{active: openDrop === 'user'}">
        <button class="user-avatar" @click="toggleDrop('user')" :title="userName">
          <span class="avatar-initial">{{ initial }}</span>
        </button>
        <div v-if="openDrop === 'user'" class="drop-menu user-menu">
          <div class="user-info">
            <div class="user-name">{{ userName }}</div>
            <span :class="'badge ' + userRole">{{ userRole }}</span>
          </div>
          <div class="menu-divider"></div>
          <a @click="openProfile('profile')"><i class="mdi mdi-account-circle-outline"></i> My profile</a>
          <a @click="openProfile('password')"><i class="mdi mdi-key-outline"></i> Change password</a>
          <a @click="openProfile('sessions')"><i class="mdi mdi-devices"></i> My sessions</a>
          <a v-if="canInstall" @click="triggerInstall"><i class="mdi mdi-download"></i> Install Paysol app</a>
          <div class="menu-divider"></div>
          <a @click="logout" class="danger-link"><i class="mdi mdi-logout"></i> Sign out</a>
        </div>
      </div>
    </div>
  </nav>

  <ProfileModal v-if="showProfile" @close="showProfile=false" />
</template>

<script>
import { paysol, execute, clearAuth, can } from '@/api';
import ProfileModal from './ProfileModal.vue';
import { refreshCount, drainQueue } from '@/offline/sync';

let searchTimer = null;

export default {
  name: 'Navigation',
  components: { ProfileModal },
  data() {
    return {
      userName: sessionStorage.getItem('userName') || '',
      userRole: sessionStorage.getItem('userRole') || '',
      isPlatformOwner: sessionStorage.getItem('isPlatformOwner') === '1',
      openDrop: null,
      // Search
      searchQ: '', searchResults: null, searchLoading: false, showSearch: false,
      // Profile modal
      showProfile: false,
      // Online / offline + install
      isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
      canInstall: typeof window !== 'undefined' && !!window.__paysolInstallPrompt,
      pendingSync: 0,
      syncing: false,
    };
  },
  computed: {
    initial() {
      return (this.userName || 'U').trim().charAt(0).toUpperCase();
    },
    hasResults() {
      const r = this.searchResults;
      if (!r) return false;
      return r.products.length + r.customers.length + r.sales.length + r.appointments.length > 0;
    },
    isRestaurantMode() {
      const bt = sessionStorage.getItem('businessType') || '';
      return bt === 'restaurant' || bt === 'mixed';
    },
    isServiceMode() {
      const bt = sessionStorage.getItem('businessType') || '';
      return bt === 'service' || bt === 'mixed';
    },
    isSales()     { return this.$route.path.startsWith('/pos') || this.$route.path.startsWith('/laybys'); },
    isRestaurant(){ return this.$route.path.startsWith('/tables') || this.$route.path.startsWith('/tabs') || this.$route.path.startsWith('/waiter') || this.$route.path.startsWith('/kitchen'); },
    isServices()  { return this.$route.path.startsWith('/appointments'); },
    isCatalog()   { return this.$route.path.startsWith('/products') || this.$route.path.startsWith('/catalog') || this.$route.path.startsWith('/barcodes') || this.$route.path.startsWith('/discounts') || this.$route.path.startsWith('/price-rules'); },
    isStock()     { return this.$route.path.startsWith('/stock'); },
    isPurchasing(){ return this.$route.path.startsWith('/suppliers') || this.$route.path.startsWith('/purchase-orders'); },
  },
  async mounted() {
    document.addEventListener('click', this.onDocClick);
    this._onOnline  = () => { this.isOnline = true; };
    this._onOffline = () => { this.isOnline = false; };
    window.addEventListener('online',  this._onOnline);
    window.addEventListener('offline', this._onOffline);
    this._onInstallable = () => { this.canInstall = true; };
    window.addEventListener('beforeinstallprompt', this._onInstallable);
    if (window.__paysolInstallPrompt) this.canInstall = true;

    // Sync banner state
    this._onSyncChanged = (e) => {
      const d = e.detail || {};
      if (d.type === 'draining') this.syncing = true;
      if (d.type === 'done')     { this.syncing = false; this.pendingSync = d.remaining || 0; }
      if (d.type === 'count')    this.pendingSync = d.pending || 0;
    };
    window.addEventListener('paysol-sync-changed', this._onSyncChanged);
    // Initial pending count
    try { this.pendingSync = await refreshCount(); } catch (e) { /* silent */ }
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocClick);
    window.removeEventListener('online',  this._onOnline);
    window.removeEventListener('offline', this._onOffline);
    window.removeEventListener('beforeinstallprompt', this._onInstallable);
    window.removeEventListener('paysol-sync-changed', this._onSyncChanged);
  },
  methods: {
    can(k) { return can(k); },
    format(n) { return Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }); },
    isActive(p) { return this.$route.path.startsWith(p); },
    toggleDrop(name) { this.openDrop = this.openDrop === name ? null : name; },
    onDocClick(e) {
      if (!e.target.closest('.drop')) this.openDrop = null;
      if (!e.target.closest('.nav-search')) this.showSearch = false;
    },
    openProfile() {
      this.openDrop = null;
      this.showProfile = true;
    },
    onSearchFocus() { if (this.searchResults) this.showSearch = true; },
    closeSearch() { this.showSearch = false; },
    onSearchInput() {
      clearTimeout(searchTimer);
      const q = this.searchQ.trim();
      if (q.length < 2) { this.searchResults = null; this.showSearch = false; return; }
      this.showSearch = true; this.searchLoading = true;
      searchTimer = setTimeout(async () => {
        try {
          const { data } = await execute({ function: 'globalSearch', q }, paysol);
          if (data.success) this.searchResults = data.data;
        } finally { this.searchLoading = false; }
      }, 250);
    },
    async triggerSync() {
      this.syncing = true;
      try { await drainQueue({ force: true }); }
      finally { this.syncing = false; }
    },
    async triggerInstall() {
      this.openDrop = null;
      const p = window.__paysolInstallPrompt;
      if (!p) return;
      try {
        p.prompt();
        const choice = await p.userChoice;
        if (choice.outcome === 'accepted') this.canInstall = false;
        window.__paysolInstallPrompt = null;
      } catch (e) { /* silent */ }
    },
    async logout() {
      try { await execute({ function: 'logout' }, paysol); } catch (e) { /* ignore */ }
      clearAuth();
      this.$router.push('/login');
    }
  }
};
</script>

<style scoped>
.nav {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 12px 24px;
  background: var(--paysol-bg-elev);
  border-bottom: 1px solid var(--paysol-line);
  position: sticky; top: 0; z-index: 20;
}
.nav-brand { cursor: pointer; display: flex; align-items: center; }
.brand-logo { height: 36px; width: auto; filter: brightness(1.1); }
.nav-items { list-style: none; display: flex; gap: 4px; margin: 0; padding: 0; flex: 1; }
.nav-items li a {
  color: var(--paysol-muted);
  padding: 8px 14px;
  border-radius: 6px;
  display: block;
  font-weight: 500;
  font-size: 13px;
}
.nav-items li a:hover { background: var(--paysol-card); color: var(--paysol-text); }
.nav-items li.active a { background: var(--paysol-teal-glow); color: var(--paysol-teal); }
.nav-items li.drop { position: relative; }
.nav-items li.drop > a { display: inline-flex; align-items: center; gap: 3px; cursor: pointer; }
.nav-items li.drop > a i { font-size: 15px; opacity: 0.6; }
.drop-menu {
  position: absolute; top: calc(100% + 6px); left: 0; min-width: 180px;
  background: var(--paysol-card); border: 1px solid var(--paysol-line);
  border-radius: 6px; box-shadow: 0 8px 20px rgba(0,0,0,0.25);
  z-index: 30; padding: 4px;
}
.drop-menu a {
  display: block; padding: 8px 12px; color: var(--paysol-text); text-decoration: none;
  border-radius: 4px; font-size: 13px;
}
.drop-menu a:hover { background: var(--paysol-teal-glow); color: var(--paysol-teal); }
.platform-link { color: var(--paysol-orange) !important; font-weight: 700 !important; }
.platform-link:hover { background: rgba(229,165,102,.1) !important; }
.platform-link i { margin-right: 4px; }
.nav-user { display: flex; align-items: center; gap: 10px; }

/* Search */
.nav-search { position: relative; display: flex; align-items: center; }
.nav-search i.mdi-magnify { position: absolute; left: 10px; color: var(--paysol-muted); font-size: 18px; pointer-events: none; }
.nav-search input {
  width: 260px; padding: 8px 12px 8px 34px; border-radius: 6px;
  background: var(--paysol-bg-elev); border: 1px solid var(--paysol-line);
  color: var(--paysol-text); font-size: 13px;
}
.nav-search input:focus { border-color: var(--paysol-teal); outline: none; box-shadow: 0 0 0 3px var(--paysol-teal-glow); width: 300px; }
.search-dropdown { position: absolute; top: calc(100% + 6px); left: 0; right: 0; z-index: 40;
  background: var(--paysol-card); border: 1px solid var(--paysol-line); border-radius: 8px;
  box-shadow: 0 12px 30px rgba(0,0,0,.25); max-height: 480px; overflow-y: auto;
}
.search-group { padding: 4px 0; }
.search-group + .search-group { border-top: 1px solid var(--paysol-line); }
.search-group-title { color: var(--paysol-muted); font-size: 10px; text-transform: uppercase; letter-spacing: 1px; padding: 8px 12px 4px; font-weight: 700; }
.search-item { display: flex; align-items: center; gap: 10px; padding: 8px 12px; text-decoration: none; color: var(--paysol-text); font-size: 13px; }
.search-item:hover { background: var(--paysol-teal-glow); color: var(--paysol-teal); }
.search-item i { color: var(--paysol-muted); font-size: 16px; flex-shrink: 0; }
.search-item code { background: var(--paysol-bg-elev); padding: 1px 5px; border-radius: 3px; color: var(--paysol-teal); font-size: 11px; }
.search-empty { padding: 20px 12px; text-align: center; color: var(--paysol-muted); font-size: 13px; }

/* User avatar dropdown */
.user-avatar-wrap { position: relative; }
.user-avatar {
  width: 36px; height: 36px; border-radius: 50%; padding: 0;
  background: linear-gradient(135deg, var(--paysol-teal), var(--paysol-teal-deep));
  color: #fff; font-weight: 700; font-size: 14px; border: 2px solid transparent;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  transition: border-color 0.15s;
}
.user-avatar:hover { border-color: var(--paysol-teal-glow); }
.user-avatar-wrap.active .user-avatar { border-color: var(--paysol-teal); }
.avatar-initial { line-height: 1; }
.user-menu { min-width: 220px; padding: 6px; }
.user-menu .user-info { padding: 10px 12px; }
.user-menu .user-name { color: var(--paysol-text); font-weight: 700; font-size: 14px; margin-bottom: 4px; }
.menu-divider { height: 1px; background: var(--paysol-line); margin: 4px 0; }
.user-menu a {
  display: flex; align-items: center; gap: 10px; padding: 8px 12px;
  cursor: pointer; color: var(--paysol-text); text-decoration: none; font-size: 13px; border-radius: 4px;
}
.user-menu a:hover { background: var(--paysol-teal-glow); color: var(--paysol-teal); }
.user-menu a i { font-size: 16px; opacity: 0.8; }
.user-menu a.danger-link { color: var(--paysol-danger); }
.user-menu a.danger-link:hover { background: rgba(239,68,68,.08); color: var(--paysol-danger); }

@media (max-width: 900px) {
  .nav-search input { width: 160px; }
  .nav-search input:focus { width: 220px; }
}

.offline-banner {
  position: sticky; top: 0; z-index: 30;
  background: var(--paysol-warning); color: #1a2332;
  padding: 8px 20px; font-size: 12px; font-weight: 600;
  display: flex; align-items: center; gap: 8px; justify-content: center;
}
.offline-banner i { font-size: 16px; }
.sync-banner {
  position: sticky; top: 0; z-index: 29;
  background: var(--paysol-teal); color: #fff;
  padding: 8px 20px; font-size: 12px; font-weight: 600;
  display: flex; align-items: center; gap: 10px; justify-content: center;
}
.sync-banner.sync-banner-syncing i { animation: sync-spin 1.5s linear infinite; }
@keyframes sync-spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
.sync-banner i { font-size: 16px; }
.sync-now-btn {
  background: rgba(255,255,255,0.2); color: #fff; border: 1px solid rgba(255,255,255,0.4);
  padding: 3px 10px; border-radius: 4px; font-size: 11px; cursor: pointer; font-weight: 600;
}
.sync-now-btn:hover { background: rgba(255,255,255,0.3); }
</style>
